import { useState } from 'react';
import Checkbox from 'src/components/primitives/Checkbox/Checkbox';
import Switch from 'src/components/primitives/Switch/Switch';
import useUpdateSubaccountSettings from 'src/hooks/velocityActions/user/useUpdateSubaccountSettings';
import useVelocityClient from 'src/hooks/useVelocityClient';
import useWalletIsConnected from 'src/hooks/useWalletIsConnected';
import useVelocityAccountStore from 'src/stores/useVelocityAccountsStore';
import { cj } from 'src/utils/classNames';
import { WithClassName } from 'src/utils/UtilityTypes';

interface Props extends WithClassName {
	isAccountMarginEnabled: boolean;
	checked: boolean;
	onCheckedChange: () => void;
}

export default function MarginCheckbox({
	isAccountMarginEnabled,
	checked,
	onCheckedChange,
	className,
}: Props) {
	const tooltip = !isAccountMarginEnabled
		? { content: <EnableMarginTooltip /> }
		: undefined;

	return (
		<Checkbox.Default
			id="trade-form-margin"
			label="Margin"
			checked={isAccountMarginEnabled && checked}
			onCheckedChange={onCheckedChange}
			tooltip={tooltip}
			disabled={!isAccountMarginEnabled}
			className={cj('w-fit', className)}
		/>
	);
}

function EnableMarginTooltip() {
	const velocityClient = useVelocityClient();
	const isWalletConnected = useWalletIsConnected();
	const isManagedAccount = useVelocityAccountStore(
		(s) => !!s.accounts[s.currentUserKey ?? '']?.isDelegatedTo
	);

	const [isSubmitting, setIsSubmitting] = useState(false);
	const { updateSubaccountSettings } = useUpdateSubaccountSettings();

	const enableAccountMargin = async () => {
		if (!velocityClient || isSubmitting) {
			return;
		}

		setIsSubmitting(true);

		await updateSubaccountSettings({
			[velocityClient.activeSubAccountId!]: { marginTradingEnabled: true },
		});

		setIsSubmitting(false);
	};

	const disabled = !isWalletConnected || isManagedAccount;

	return (
		<div className="flex items-center gap-x-2xl typo-label-xs">
			Trade with up to 5x leverage with spot margin.
			<Switch
				size="sm"
				className="shrink-0"
				onCheckedChange={enableAccountMargin}
				disabled={disabled}
			/>
		</div>
	);
}
